// @flow
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { CSSTransitionGroup } from 'react-transition-group';
import { debounce } from 'lodash';
import { OrderedMap } from 'immutable';
import cx from 'classnames';
import idx from 'idx';

import {
  changeQuery,
  loadGroupChats,
  loadPotentialChatMembers,
  createGroupChatRecipient,
  selectRecipient,
  flush,
  RECIPIENT_TYPES,
  searchGroupChats,
} from 'reducers/roleBasedMessagingSearch';
import { MODAL_FILE_CONTEXT_KEY, removeFile } from 'reducers/fileUploads';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import Chats from 'collections/Chats';
import ChatStore from 'chat/stores/ChatStore';
import Dialog from 'components/Dialog';
import EventTracking from 'lib/EventTracking';
import GroupMessageStarter from 'chat/components/GroupMessageStarter';
import Heading from 'components/Heading';
import LayerTrigger from 'components/LayerTrigger';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import { mountModal } from 'lib/modal';
import Row from 'components/Row';
import ScrollContainer from 'components/ScrollContainer';
import SearchBlock from 'components/SearchBlock';
import SettingsStore from 'stores/SettingsStore';
import { SEARCH_NETWORK_LIMIT_MS } from 'chat/constants/ChatConstants';
import RecipientItem from './RecipientItem';
import RecipientSelectionItem from './RecipientSelectionItem';
import Tooltip from 'components/Tooltip';
import type { MessageType } from './SplitComposer';
import type { UploadRequestData } from 'reducers/fileUploads';
import type { UIEventContext } from './ComposerContext';

import '../SplitComposer.scss';

type Props = {
  onContinue: Function,
  loadingGroupResults: boolean,
  loadingPeopleResults: boolean,
  messageType: MessageType,
  isUrgentMessage: boolean,
  uiEventContext: UIEventContext,

  // Redux state
  query: string,
  results: Array<Recipient[]>,
  selection: Recipient[],
  districtIsSelected: boolean,
  fileUploads: OrderedMap<string, UploadRequestData>,
  savedMessage: string,
  totalRecipientCount: number,

  // Redux dispatchers
  onChangeQuery: (query: string) => *,
  onFlush: () => *,
  onSelectRecipient: (recipient: Recipient, filter: ?MessageFilterType) => *,
  onLoadGroupChats: (chats: Chats) => *,
  onLoadPotentialChatMembers: (query: string) => *,
  onSearchGroupChats: (query: string) => *,
  removeFile: (fileContextKey: string, filename: string) => void,
  recipientCountsMap: RecipientCountsMap,
};

export function DisabledTooltipLayer(props: { text: any, children: * }) {
  return (
    <LayerTrigger layer={<Tooltip>{props.text}</Tooltip>} placement="bottom" alignment="middle">
      <div>{props.children}</div>
    </LayerTrigger>
  );
}

class SelectFromList extends React.Component<Props, *> {
  componentDidMount() {
    EventTracking.trackKey('announcements.share_to.view', this.props.uiEventContext);
  }

  _searchBlock: { handleClose: () => void };

  debouncedLoadPotentialChatMembers = debounce((query: string) => {
    const { onLoadPotentialChatMembers, onSearchGroupChats } = this.props;
    onLoadPotentialChatMembers(query);
    onSearchGroupChats(query);
  }, SEARCH_NETWORK_LIMIT_MS);

  handleQueryChange = debounce(
    (rawQuery: string = '') => {
      const query = rawQuery.trim();
      if (query !== '') {
        this.debouncedLoadPotentialChatMembers(query);
      }
      if (query === '') {
        this.props.onChangeQuery(query);
      }
    },
    500,
    { leading: false, trailing: true }
  );

  removeAllFiles = () => {
    const { fileUploads } = this.props;
    fileUploads
      .keySeq()
      .forEach(filename => this.props.removeFile(MODAL_FILE_CONTEXT_KEY, filename));
  };

  renderHeader = (
    title: string,
    options: { disabled?: boolean, hidden: boolean }
  ): React.Element<any> => {
    if (options.hidden) {
      return null;
    } else {
      return (
        <Heading
          align="left"
          level="3"
          className={cx('SelectFromList-resultHeader', {
            // prettier-ignore
            'SelectFromList-resultHeader--disabled': options.disabled
          })}>
          {title}
        </Heading>
      );
    }
  };

  nonDistrictRecipientIsSelected = () => {
    const { selection, districtIsSelected } = this.props;
    return !!selection.length && !districtIsSelected;
  };

  getRecipients = (
    results: Array<Recipient[]>,
    type: 'organization' | 'group' | 'groupChat' | 'individual'
  ): Recipient[][] => {
    switch (type) {
      case 'organization':
        return results.filter(result =>
          result.every(r => {
            return r.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT;
          })
        );
      case 'group':
        return results.filter(result =>
          result.every(r => {
            return r.type === RECIPIENT_TYPES.GROUP_RECIPIENT;
          })
        );
      case 'groupChat':
        return results.filter(result =>
          result.every(r => {
            return r.type === RECIPIENT_TYPES.GROUP_CHAT_RECIPIENT;
          })
        );
      case 'individual':
        return results.filter(result =>
          result.every(r => {
            return r.type === RECIPIENT_TYPES.INDIVIDUAL_RECIPIENT;
          })
        );
      default:
        console.error('SelectFromList - getRecipients called without type');
        return [];
    }
  };

  checkIfRecipientIsSelected = (recipient: Recipient[]) => {
    const recipientIds = recipient.map(r => r.id);
    const selectionIds = this.props.selection.map(s => s.id);

    return !!recipientIds.filter(rId => selectionIds.indexOf(rId) !== -1).length;
  };

  toggleNewGroupChatModal = () => {
    const { onLoadGroupChats, onSelectRecipient, uiEventContext } = this.props;
    const additionalChatMembersLimit = SettingsStore.getChatMembersLimit() - 1;
    EventTracking.trackKey('announcements.share_to.group_chat.add_new.click', uiEventContext);
    const unmountModal = mountModal(
      <Dialog
        title="Create a group conversation"
        size="large"
        subtext={`Start the dialogue with up to ${additionalChatMembersLimit} other people—everyone can participate in the conversation.`}
        textAlign="left">
        <BoxBody>
          <GroupMessageStarter
            additionalChatMembersLimit={additionalChatMembersLimit}
            createRemoteChat
            onCreateRemoteChat={chat => {
              const recipient = createGroupChatRecipient(chat);
              onSelectRecipient(recipient, undefined);
              onLoadGroupChats(ChatStore.getGroupChats());
            }}
            onRequestHide={() => {
              if (unmountModal) {
                unmountModal();
              }
            }}
          />
        </BoxBody>
      </Dialog>,
      this
    );
  };

  renderResultsList = () => {
    const {
      results,
      districtIsSelected,
      query,
      loadingPeopleResults,
      selection,
      messageType,
    } = this.props;

    const isUrgentMessage = messageType === 'urgent';

    // this is used for filtering out people that shouldn't be targets for urgent messages
    const getShouldShowRecipientFilter = (urgentMessageFlag: boolean): (Recipient => boolean) => {
      if (urgentMessageFlag === false) {
        // making very sure we don't do anything different for normal messages
        return () => true;
      } else {
        return (recipient: Recipient) => {
          if (recipient.type === RECIPIENT_TYPES.GROUP_RECIPIENT) {
            return !!idx(recipient, _ => _.group.organization.permissions.canReceiveUrgentMessage);
          } else if (recipient.type === RECIPIENT_TYPES.ORGANIZATION_RECIPIENT) {
            return recipient.organization.permissions.canReceiveUrgentMessage;
          }
          return false;
        };
      }
    };

    const shouldShowRecipientFilter = getShouldShowRecipientFilter(isUrgentMessage);
    const districtResults = this.getRecipients(results, 'organization').filter(result =>
      result.every(shouldShowRecipientFilter)
    );
    const schoolResults = this.getRecipients(results, 'group').filter(result =>
      result.every(r =>
        r.type === RECIPIENT_TYPES.GROUP_RECIPIENT
          ? r.group.type === 'organization' &&
            idx(r, _ => _.group.permissions.can_post) &&
            shouldShowRecipientFilter(r)
          : false
      )
    );
    const groupResults = this.getRecipients(results, 'group').filter(result =>
      result.every(r =>
        r.type === RECIPIENT_TYPES.GROUP_RECIPIENT
          ? r.group.type === 'owned' &&
            idx(r, _ => _.group.permissions.can_post) &&
            shouldShowRecipientFilter(r)
          : false
      )
    );

    const recentGroupChatResults = this.getRecipients(results, 'groupChat')
      .filter(result => result.every(shouldShowRecipientFilter))
      .slice(0, 3);

    const recentIndividualResults = this.getRecipients(results, 'individual')
      .filter(result => result.every(shouldShowRecipientFilter))
      .slice(0, 20);

    return (
      <React.Fragment>
        {districtResults && districtResults.length ? (
          <Row size="medium">
            {this.renderHeader('Districts', {
              disabled: this.nonDistrictRecipientIsSelected(),
              hidden: false,
            })}
            <ul>
              {districtResults.map(district => (
                <RecipientItem
                  key={district[0].id}
                  recipient={district}
                  recipientIsSelected={this.checkIfRecipientIsSelected(district)}
                  nonDistrictRecipientIsSelected={this.nonDistrictRecipientIsSelected()}
                  selectionLength={selection.length}
                  districtIsSelected={districtIsSelected}
                />
              ))}
            </ul>
          </Row>
        ) : null}

        {schoolResults && schoolResults.length ? (
          <Row size="medium">
            {this.renderHeader('Schools', { disabled: districtIsSelected, hidden: false })}
            <ul>
              {schoolResults.map(school => (
                <RecipientItem
                  key={school[0].id}
                  recipient={school}
                  recipientIsSelected={this.checkIfRecipientIsSelected(school)}
                  selectionLength={selection.length}
                  districtIsSelected={districtIsSelected}
                />
              ))}
            </ul>
          </Row>
        ) : null}

        {groupResults && groupResults.length ? (
          <Row size="medium">
            {this.renderHeader('Classes', { disabled: districtIsSelected, hidden: false })}
            <ul>
              {groupResults.map(group => (
                <RecipientItem
                  key={group[0].id}
                  recipient={group}
                  recipientIsSelected={this.checkIfRecipientIsSelected(group)}
                  selectionLength={selection.length}
                  districtIsSelected={districtIsSelected}
                />
              ))}
            </ul>
          </Row>
        ) : null}

        {((recentGroupChatResults && recentGroupChatResults.length) || !query) && (
          <Row size="medium">
            <MediaObject figure="right" center>
              {this.renderHeader('Recent Group Conversations', {
                disabled: districtIsSelected,
                hidden: isUrgentMessage,
              })}
              <Heading
                level="3"
                className={cx('SelectFromList-resultHeader SelectFromList-resultHeader--right', {
                  // prettier-ignore
                  'SelectFromList-resultHeader--disabled': districtIsSelected
                })}>
                {isUrgentMessage === false ? (
                  <Link onClick={this.toggleNewGroupChatModal}>Create New</Link>
                ) : null}
              </Heading>
            </MediaObject>
            {recentGroupChatResults && recentGroupChatResults.length ? (
              <ul>
                {recentGroupChatResults.map(groupChat => (
                  <RecipientItem
                    key={groupChat[0].id}
                    recipient={groupChat}
                    recipientIsSelected={this.checkIfRecipientIsSelected(groupChat)}
                    selectionLength={selection.length}
                    districtIsSelected={districtIsSelected}
                  />
                ))}
              </ul>
            ) : null}
          </Row>
        )}

        {recentIndividualResults && recentIndividualResults.length && isUrgentMessage === false ? (
          <React.Fragment>
            <Row size="medium">
              {this.renderHeader('People', {
                disabled: districtIsSelected,
                hidden: isUrgentMessage,
              })}
              <ul>
                {recentIndividualResults.map(individual => (
                  <RecipientItem
                    key={individual[0].id}
                    recipient={individual}
                    recipientIsSelected={this.checkIfRecipientIsSelected(individual)}
                    selectionLength={selection.length}
                    districtIsSelected={districtIsSelected}
                  />
                ))}
                {/* Because v2/chats/members/potential is the slowest query, we
                  don't gate all of the other results on it */}
                {loadingPeopleResults && (
                  <li className="RecipientItem">
                    <CenteredLoadingIndicator />
                  </li>
                )}
              </ul>
            </Row>
          </React.Fragment>
        ) : null}
      </React.Fragment>
    );
  };

  render() {
    const {
      selection,
      totalRecipientCount,
      loadingGroupResults,
      recipientCountsMap,
      uiEventContext,
    } = this.props;

    return (
      <React.Fragment>
        <div className="SelectFromList-main">
          <SearchBlock
            ref={ref => {
              if (ref && ref.handleClose) {
                this._searchBlock = ref;
              }
            }}
            className="SelectFromList-main-search"
            label="Search for people or classes"
            onDismiss={this.handleQueryChange}
            onQueryChange={this.handleQueryChange}
            onClick={() =>
              EventTracking.trackKey('announcements.share_to.search.click', uiEventContext)
            }
          />
          <ScrollContainer>
            {loadingGroupResults ? <CenteredLoadingIndicator /> : this.renderResultsList()}
          </ScrollContainer>
        </div>
        <div className="SelectFromList-sidebar">
          <ScrollContainer>
            <React.Fragment>
              <Row size="xsmall">
                <MediaObject figure="right" center>
                  <Heading level="4">Selected</Heading>
                  <Heading level="4">{totalRecipientCount || ''}</Heading>
                </MediaObject>
              </Row>
              {selection.length ? (
                <ul>
                  <CSSTransitionGroup
                    transitionName="SelectFromList-selectionItem-transition"
                    transitionEnterTimeout={200}
                    transitionLeaveTimeout={200}>
                    {selection.map(s => (
                      <RecipientSelectionItem
                        key={s.id}
                        selection={s}
                        count={recipientCountsMap.get(`${s.id}`)}
                      />
                    ))}
                  </CSSTransitionGroup>
                </ul>
              ) : null}
            </React.Fragment>
          </ScrollContainer>
          <div className="SelectFromList-sidebar-footer">
            <Button
              onClick={() => {
                EventTracking.trackKey('announcements.share_to.next.click', uiEventContext);
                this.props.onContinue();
              }}
              disabled={!selection.length}
              data-test-selector="split-composer-continue-button">
              Continue
            </Button>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: Object) => {
  const { roleBasedMessagingSearch, fileUploads } = state;
  const fileGroup = fileUploads.get(MODAL_FILE_CONTEXT_KEY);
  return {
    results: roleBasedMessagingSearch.results,
    query: roleBasedMessagingSearch.query,
    selection: roleBasedMessagingSearch.selection,
    districtIsSelected: roleBasedMessagingSearch.districtIsSelected,
    savedMessage: roleBasedMessagingSearch.message,
    fileUploads: fileGroup || new OrderedMap(),
    totalRecipientCount: roleBasedMessagingSearch.totalRecipientCount,
    recipientCountsMap: roleBasedMessagingSearch.recipientCountsMap,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return bindActionCreators(
    {
      onChangeQuery: changeQuery,
      onLoadGroupChats: loadGroupChats,
      onLoadPotentialChatMembers: loadPotentialChatMembers,
      onSearchGroupChats: searchGroupChats,
      onSelectRecipient: selectRecipient,
      onFlush: flush,
      removeFile,
    },
    dispatch
  );
};

export { SelectFromList };

export default connect(mapStateToProps, mapDispatchToProps)(SelectFromList);
